import React, { useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

import ConfirmationDialog from '../components/ConfirmDialogs/ConfirmationDialog';

import useTimberSalesPriceController from './CustomHooks/useTimberSalesPrice';
import { DEFAULT_TABLE_LEFT_SIDE_LABEl, variablesInputfields } from './Data/SalesPriceFormInputfields';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import useTimberSalesPricingListController from '../TimberSalesPricingList/CustomHooks/useTimberSalesPriceListController';

// Lable Hook
import useLabel from './CustomHooks/useLable';
import Fullscreen from './components/FullScreen';
import { Star } from '@mui/icons-material';
import PrevPricingVersionCard from './PrevPricingVersionCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const FULL_PRICE_SUMMARY = [
  {
    key: 'totalSalesSumOfAllProduct',
    label: 'Sales Price',
  },
  {
    key: 'totalPurchaseSumOfAllProduct',
    label: 'Purchase Price',
  },
  {
    key: 'totalHFCostAllProduct',
    label: 'H/F Cost',
  },
  // {
  //   key: 'totalProductionCostSumOfAllProduct',
  //   label: 'Cost',
  // },
  {
    key: 'totalProfitSumOfAllProduct',
    label: 'Profit',
  },
];

const autoCompleteSlotProps = {
  paper: {
    sx: {
      '& .MuiAutocomplete-option': {
        fontSize: '0.82rem',
      },
    },
  },
};
const formatter = new Intl.NumberFormat('en-GB');

const getExcelInputSx = isReadOnly => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: isReadOnly ? 'transparent' : '#FFFBE6',
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '4px 6px',
    fontSize: '0.8rem',
    lineHeight: 1.2,
  },

  '& .Mui-disabled': {
    opacity: 1, // remove fade
  },

  '& .MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: '#000', // text color (Chrome/Safari)
    color: '#000', // Firefox
  },
});

const COLUMN_WIDTH = 200;
const FIT_COLUMNS = 10;

// Add this new constant for all table cells
const excelCellSx = {
  padding: '2px 4px !important', // Minimal padding
  height: '32px',
  fontSize: '0.8rem',
  borderRight: '1px solid #e0e0e0',
  borderBottom: '1px solid #e0e0e0',
  lineHeight: 1.2,
  verticalAlign: 'middle',
  minWidth: COLUMN_WIDTH,
  maxWidth: COLUMN_WIDTH,
  width: COLUMN_WIDTH,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const stickyLeftCellSx = {
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: '#fff',
};

const SummaryCard = ({
  title,
  value,
  editable = false,
  onChange,
  color,
  updatedMinWidth,
  boldValue,
  singleLine = false, // ← new prop
}) => {
  const [editing, setEditing] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value ?? '');
  const inputRef = React.useRef(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const valueRef = React.useRef(null);

  React.useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  const handleFinishEditing = () => {
    setEditing(false);
    onChange(localValue);
  };

  // Only show tooltip when text is actually truncated
  const handleMouseEnter = () => {
    if (singleLine && valueRef.current) {
      const el = valueRef.current;
      if (el.scrollWidth > el.clientWidth) {
        setShowTooltip(true);
      }
    }
  };

  return (
    <Paper
      elevation={0}
      tabIndex={-1}
      onClick={e => {
        if (e.detail === 0) return;
        editable && !editing && setEditing(true);
        setTimeout(() => {
          if (inputRef.current) inputRef.current.focus();
        }, 0);
      }}
      sx={{
        px: 1.5,
        py: 0.5,
        minWidth: updatedMinWidth ?? 100,
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        backgroundColor: color ?? (editable ? '#FFFBE6' : '#f9fafb'),
        transition: 'all 0.2s',
        cursor: editable && !editing ? 'pointer' : 'default',
        '&:hover':
          editable && !editing
            ? { boxShadow: '0 2px 4px rgba(0,0,0,0.1)', '& .valueText': { textDecoration: 'underline' } }
            : {},
      }}>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.7rem',
          color: '#6b7280',
          fontWeight: 500,
          letterSpacing: '0.02em',
          display: 'block',
          mb: 0,
        }}>
        {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {!editable ? (
          <Tooltip title={singleLine ? value ?? '' : ''} placement="top" disableHoverListener={!singleLine} arrow>
            <Typography
              ref={valueRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setShowTooltip(false)}
              sx={{
                fontSize: '0.85rem',
                userSelect: 'none',
                fontWeight: boldValue ? 600 : 400,
                ...(singleLine && {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: updatedMinWidth ? updatedMinWidth - 24 : 76,
                }),
              }}>
              {value ?? '-'}
            </Typography>
          </Tooltip>
        ) : (
          <Tooltip
            title={singleLine && !editing ? localValue ?? '' : ''}
            placement="top"
            disableHoverListener={!singleLine || editing}
            arrow>
            <TextField
              inputRef={inputRef}
              size="small"
              value={localValue}
              onChange={e => setLocalValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Tab' || e.key === 'Enter') {
                  e.key === 'Enter' && e.preventDefault();
                  handleFinishEditing();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setLocalValue(value ?? '');
                  setEditing(false);
                } else if (e.key === 'Backspace') {
                  if (e.target.value.length <= 1) {
                    setTimeout(() => e.target.select(), 0);
                  }
                }
              }}
              onFocus={e => {
                if (!editing) setEditing(true);
                if (localValue === '0' || localValue === 0) {
                  setTimeout(() => e.target.select(), 0);
                }
              }}
              onBlur={handleFinishEditing}
              sx={{
                width: updatedMinWidth ?? 70,
                '& .MuiInputBase-root': {
                  height: 24,
                  fontSize: '0.8rem',
                  backgroundColor: editing ? '#FFFBE6' : 'transparent',
                  borderRadius: editing ? '4px' : 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: editing ? '1px solid #d1d5db' : 'none',
                },
                '& .MuiInputBase-input': {
                  cursor: editing ? 'text' : 'pointer',
                  padding: editing ? '4px 6px' : 0,
                  ...(singleLine &&
                    !editing && {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }),
                },
              }}
              InputProps={{ readOnly: !editing }}
            />
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};

const TimberSalesPriceForm = props => {
  const { siteAssessmentId, pricingVersion } = useParams();

  const {
    openConfirmationDialog,
    setOpenConfirmationDialog,
    confirmationDialogProps,
    allSiteAssesments,
    selectedSiteAssesment,
    setSeletedSiteAssesment,
    siteAssessmentVariables,
    selectedSiteProducts,
    salesPricingData,
    setSalesPricingData,
    handlePriceIpChange,
    handleSalesPriceDataChange,
    savePricingData,
    handleLPGRateEdit,
    siteAssesmentUpdateVariables,
    setSiteAssessmnetUpdateVariables,
    handleSiteAssesmentVariableEdit,
    productListOptions,
    fullPriceForSite,
    closePricingVersion,
    handleProductDelete,
    productConformationDialog,
    setProductConformationDialog,
    currentProduct,
    allLocationData,
    currentSpNumber,
    selectedSiteInfo,
    getDistanceKmByName,
    showPrevVersions,
    openDrawer,
    closeDrawer,
    prevVersions,
    calculateTotalProductPercent,
    approveAllRoutes,
    haulgeCategoryOptions,
    handleHaulageCategoryChange,
    rateParams,
  } = useTimberSalesPriceController({ siteAssessmentId, pricingVersion });

  const hasPendingRoutes = useMemo(() => {
    if (!allLocationData?.length || !salesPricingData?.length) return false;

    return salesPricingData.some(row => {
      const dest = row?.destination || row?.defaultDestination;
      if (!dest) return false;
      const loc = allLocationData.find(l => l.destinationName === dest);
      return loc && loc.distanceSource !== 'route';
    });
  }, [salesPricingData, allLocationData]);

  const isReadOnly = !(selectedSiteAssesment && currentSpNumber);

  const getLocationOption = destinationName => {
    if (!destinationName) return null;
    return ALL_LOCATION.find(opt => opt.value === destinationName) || null;
  };

  const MAX_COLUMNS = 15;

  const lastFilledIndex = React.useMemo(() => {
    for (let i = salesPricingData.length - 1; i >= 0; i--) {
      if (salesPricingData[i]?.product) return i;
    }
    return -1;
  }, [salesPricingData]);

  const visibleCol = isReadOnly ? lastFilledIndex + 1 : lastFilledIndex + 2; // Empty Column

  const visibleColumnsCount = Math.min(visibleCol, MAX_COLUMNS);
  const visibleColumns = React.useMemo(() => Array.from({ length: visibleColumnsCount }, (_, i) => i), [
    visibleColumnsCount,
  ]);

  const renderColumns = renderCell =>
    visibleColumns.map(i => {
      const curr = i + 1; // 1-based
      return <React.Fragment key={curr}>{renderCell(curr)}</React.Fragment>;
    });

  const tableMinWidth = React.useMemo(() => {
    switch (visibleColumnsCount) {
      case 1:
        return 480;
      case 2:
        return 720;
      case 3:
        return 960;
      default:
        if (visibleColumnsCount <= FIT_COLUMNS) {
          return '100%';
        }
        return visibleColumnsCount * COLUMN_WIDTH;
    }
  }, [visibleColumnsCount]);

  console.log(selectedSiteInfo);
  const isExtractionOver300 = useMemo(() => {
    const value = selectedSiteInfo?.harvesting?.extractionOver300meters;
    return value?.toLowerCase() === 'yes';
  }, [selectedSiteInfo]);

  const ALL_LOCATION = useMemo(() => {
    if (!allLocationData) return [];

    return allLocationData.map(loc => ({
      label: `${loc.destinationName} (${loc.distanceKm} km)`,
      value: loc.destinationName,
      distanceSource: loc.distanceSource,
    }));
  }, [allLocationData]);

  // const { handleConvertToContract } = useTimberSalesPricingListController();

  const getRow7Data = currdata => {
    let currRowPer = currdata.productPercent || 0;
    // let data = (siteAssesmentUpdateVariables.hectares * siteAssesmentUpdateVariables.tonnesPerHectare * currRowPer) / 100;
    let data = (siteAssesmentUpdateVariables?.totalTons * currRowPer) / 100;
    return formatter.format(data.toFixed(0));
  };
  const getRow8Data = currdata => {
    let currRowPer = currdata.productPercent || 0;
    // let data = (siteAssesmentUpdateVariables.tonnesPerHectare * currRowPer) / 100;
    let data = (siteAssesmentUpdateVariables?.totalM3 * currRowPer) / 100;
    return formatter.format(data.toFixed(0));
  };

  const {
    anchorEl,
    open,
    labelText,
    setLabelText,
    handleMenuClick,
    handleClose,
    handleAddLabel,
    setLabelError,
    labelError,
    setLabels,
    currentLable,
  } = useLabel(siteAssessmentId, pricingVersion);

  const getIconComponent = () => {
    // const handleConvert = () => {
    //   handleClose();
    //   handleConvertToContract({
    //     siteAssessmentID: siteAssessmentId,
    //     pricingVersion,
    //   });
    // };
    const handleHome = () => {
      handleClose();
      window.history.back();
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={handleMenuClick}
          disableRipple
          disableFocusRipple
          sx={{
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={handleHome}>Home</MenuItem>
          {/* <MenuItem onClick={handleConvert}>Convert To Contract</MenuItem> */}
          <MenuItem
            onClick={() => {
              handleClose();
              openDrawer();
            }}>
            View Previous Versions
          </MenuItem>
          <MenuItem onClick={() => window.open('/rateCard', '_blank')} target="_blank">
            View Rate Card
          </MenuItem>
        </Menu>
      </>
    );
  };

  console.log(selectedSiteAssesment, 'datam');

  return (
    <div className="app-wrapper siteWrapper" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {isReadOnly && (
        <Alert
          variant="filled"
          severity="info"
          sx={{
            mb: 1,
            borderRadius: '8px',
            alignItems: 'center',
          }}>
          This pricing version is <strong>read-only </strong>
          as there is contract attached to it
        </Alert>
      )}

      {hasPendingRoutes && (
        <Alert
          variant="filled"
          severity={'error'}
          sx={{
            mb: 1,
            borderRadius: '8px',
            alignItems: 'center',
            '& .MuiAlert-message': { width: '100%' },
            '& .MuiAlert-action': { alignItems: 'center', paddingTop: 0 },
          }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'nowrap' }}>
              <Button
                size="small"
                variant="contained"
                onClick={approveAllRoutes}
                sx={{
                  backgroundColor: '#fff',
                  color: 'error.main',
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}>
                Approve All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  window.open(`/siteAssessment?siteAssessmentId=${siteAssessmentId}`, '_blank');
                }}
                sx={{ color: '#fff', borderColor: '#fff', whiteSpace: 'nowrap', width: 160 }}>
                Show Route Map
              </Button>
            </Box>
          }>
          Some destinations don't have saved Google routes yet. Distances shown are estimated.
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 1,
          }}>
          {selectedSiteAssesment?.wh_numberName && (
            <Box sx={{ width: 150 }}>
              <SummaryCard
                title={selectedSiteAssesment?.contractUniqueID}
                value={selectedSiteAssesment?.wh_numberName || ''}
                editable={false}
                updatedMinWidth={150}
                singleLine
              />
            </Box>
          )}
          <Box sx={{ width: 200 }}>
            <SummaryCard
              title={pricingVersion || 'Pricing Version'}
              value={currentLable || 'Default Version'}
              editable={!isReadOnly}
              onChange={val => {
                if (!val || !val.trim()) return;
                setLabelText(val); // optional, for local state
                handleAddLabel(siteAssessmentId, pricingVersion, val);
              }}
              updatedMinWidth={200}
            />
          </Box>

          {/* MIDDLE — summary cards */}
          {selectedSiteAssesment && (
            <Box sx={{ flex: 1, overflowX: 'auto' }}>
              <Grid container spacing={1} wrap="nowrap">
                {variablesInputfields.map(variableInput => (
                  <Grid item key={variableInput.name}>
                    <SummaryCard title={variableInput.label} value={siteAssessmentVariables[variableInput.name]} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>

        {/* <CustomContainerHeader match={props.match} title={showTitleWithContract()} iconComponent={getIconComponent()} /> */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Left: icons */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Fullscreen />
            {getIconComponent()}
          </Box>

          {/* Right: actions */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {selectedSiteAssesment && currentSpNumber && (
              <Button onClick={savePricingData} variant="contained" size="small">
                Save
              </Button>
            )}
            {selectedSiteAssesment && (
              <Button onClick={closePricingVersion} variant="outlined" size="small">
                Close
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {selectedSiteAssesment && (
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={1.3}>
              <SummaryCard
                title="Current Fuel Price"
                value={rateParams[0]['fuelCurrentRate']}
                editable={isReadOnly}
                // onChange={value => handleLPGRateEdit(value)}
              />
            </Grid>

            {variablesInputfields.map(variableInput => (
              <Grid item xs={12} sm={1.3} key={variableInput.name}>
                <SummaryCard
                  title={variableInput.label}
                  value={siteAssesmentUpdateVariables[variableInput.name]}
                  editable={variableInput?.isEditable && !isReadOnly}
                  onChange={
                    variableInput?.isEditable ? value => handleSiteAssesmentVariableEdit(variableInput?.name, value) : null
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={10}>
          {selectedSiteAssesment && (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '8px',
                overflow: 'auto',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                height: 'calc(100vh - 220px)',
              }}>
              <Table
                stickyHeader
                className="mui-table"
                sx={{
                  width: 'max-content',
                  minWidth: tableMinWidth,
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    ...excelCellSx, // Apply to all cells
                  },
                }}>
                <TableHead>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Product
                    </TableCell>
                    {renderColumns(curr => {
                      return (
                        <TableCell size="small" key={curr}>
                          <Tooltip title={salesPricingData[curr - 1] ? salesPricingData[curr - 1].product : ''}>
                            <Autocomplete
                              disabled={isReadOnly}
                              options={productListOptions}
                              disableClearable
                              size="small"
                              fullWidth
                              value={salesPricingData[curr - 1]?.product || null}
                              sx={{
                                fontSize: '0.7rem',
                                '& .MuiAutocomplete-inputRoot': {
                                  minWidth: '150px',
                                },
                              }}
                              onChange={(e, newValue) => {
                                const colIndex = curr - 1;

                                handleSalesPriceDataChange('product', newValue, colIndex);

                                // 👇 Set default extraction cost ONLY ONCE
                                if (
                                  newValue &&
                                  colIndex > 0 &&
                                  !salesPricingData[colIndex]?.extractionCosts &&
                                  salesPricingData[colIndex - 1]?.extractionCosts != null
                                ) {
                                  handleSalesPriceDataChange(
                                    'extractionCosts',
                                    salesPricingData[colIndex - 1].extractionCosts,
                                    colIndex,
                                  );
                                }
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  fullWidth
                                  sx={getExcelInputSx(isReadOnly)}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {salesPricingData[curr - 1]?.product && (
                                          <IconButton size="small" onClick={() => handleProductDelete(curr - 1)}>
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        )}

                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              slotProps={{ ...autoCompleteSlotProps }}
                            />
                          </Tooltip>
                        </TableCell>
                      );
                    })}

                    <ConfirmationDialog
                      confirmationFun={() => handleSalesPriceDataChange('product', null, currentProduct)}
                      open={productConformationDialog}
                      confirmationText="Do you want to delete this product. You need to save pricing to save chages."
                      setOpen={setProductConformationDialog}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell width="10%" size="small" sx={stickyLeftCellSx}>
                      Destination
                    </TableCell>

                    {renderColumns((curr, index) => {
                      const row = salesPricingData?.[curr - 1];
                      const loct = getDistanceKmByName(row?.defaultDestination);

                      return (
                        <TableCell
                          size="small"
                          key={index}
                          sx={{
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            fontSize: '0.85rem',
                          }}>
                          {row?.defaultDestination ? `${row.defaultDestination} (${loct?.distanceKm ?? 0} km)` : '--'}

                          {loct.distanceSource === 'route' && <Chip label="✓" size="small" color="success" />}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell width={'10%'} size="small" sx={stickyLeftCellSx}>
                      Price
                    </TableCell>

                    {renderColumns((curr, index) => {
                      return (
                        <TableCell
                          size="small"
                          key={`${index}`}
                          sx={{
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            fontSize: '0.85rem',
                          }}>
                          {salesPricingData[curr - 1] ? salesPricingData[curr - 1].price : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      {' '}
                      Product % ({calculateTotalProductPercent()}%)
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell size="small" key={curr}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1].product ? (
                            <TextField
                              onFocus={e => {
                                if (e.target.value === '0' || e.target.value === 0) {
                                  setTimeout(() => e.target.select(), 0); // select only if 0
                                }
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Backspace') {
                                  const value = e.target.value;
                                  if (value.length <= 1) {
                                    setTimeout(() => e.target.select(), 0);
                                  }
                                }
                              }}
                              value={salesPricingData[curr - 1].productPercent || 0}
                              type="number"
                              fullWidth
                              size="small"
                              name={`${salesPricingData[curr - 1].product}_Price`}
                              sx={getExcelInputSx(isReadOnly)}
                              inputProps={{
                                readOnly: isReadOnly,
                                min: 0,
                                max: 100,
                                style: {
                                  fontSize: '0.85rem',
                                },
                              }}
                              onChange={
                                salesPricingData[curr - 1]
                                  ? e => {
                                      let value = Number(e.target.value);

                                      if (value > 100) value = 100;
                                      if (value < 0) value = 0;

                                      handleSalesPriceDataChange('productPercent', value, curr - 1);
                                    }
                                  : () => {}
                              }
                              slotprops={{
                                input: {
                                  startAdornment: <InputAdornment position="end">%</InputAdornment>,
                                },
                              }}
                            />
                          ) : (
                            ''
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}></TableCell>

                    {renderColumns(curr => {
                      const data = salesPricingData[curr - 1];
                      if (!data || !data.product) return <TableCell key={curr} />;

                      const tonns = getRow7Data(data);
                      const tonnsPerHa = getRow8Data(data);

                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            fontSize: '0.85rem',
                          }}>
                          {`${tonns} tons (${tonnsPerHa} M³)`}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {Array.from({ length: DEFAULT_TABLE_LEFT_SIDE_LABEl.length }, (_, i) => i + 1).map(row => {
                    return (
                      <TableRow key={`${row}`}>
                        <TableCell sx={stickyLeftCellSx}>
                          {DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1].key === 'extractionCosts'
                            ? `Extraction Costs/Ton${isExtractionOver300 ? ' (Over 300)' : ''}`
                            : DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1].label}
                        </TableCell>

                        {DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1]['key'] == 'destination'
                          ? renderColumns(col => {
                              return (
                                <TableCell style={{ width: '15%', minWidth: 200 }} size="small" key={`${row}_${col}`}>
                                  <Tooltip
                                    title={salesPricingData[col - 1]?.[DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1]?.key] ?? ''}>
                                    <Autocomplete
                                      disabled={isReadOnly}
                                      options={ALL_LOCATION}
                                      disableClearable
                                      size="small"
                                      fullWidth
                                      value={
                                        getLocationOption(
                                          salesPricingData[col - 1]?.[DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1].key],
                                        ) ?? null
                                      }
                                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                                      onChange={(e, newValue) =>
                                        handleSalesPriceDataChange(
                                          DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1].key,
                                          newValue?.value ?? null,
                                          col - 1,
                                        )
                                      }
                                      renderOption={(props, option) => (
                                        <li {...props} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                          <span>{option.label}</span>
                                          {option.distanceSource === 'route' && (
                                            <Chip label="✓" size="small" color="success" />
                                          )}
                                        </li>
                                      )}
                                      renderInput={params => (
                                        <TextField {...params} size="small" fullWidth sx={getExcelInputSx(isReadOnly)} />
                                      )}
                                    />
                                  </Tooltip>
                                </TableCell>
                              );
                            })
                          : DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1]['key'] == 'haulageCategory'
                          ? renderColumns(col => {
                              return (
                                <TableCell size="small" key={`${row}_${col}`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Autocomplete
                                      fullWidth
                                      size="small"
                                      readOnly={isReadOnly}
                                      options={haulgeCategoryOptions} // e.g. [{ label: 'Option A', value: 1 }, { label: 'Option B', value: 2 }]
                                      getOptionLabel={option => option.label ?? ''}
                                      isOptionEqualToValue={(option, value) => option.value === value.value}
                                      value={
                                        salesPricingData[col - 1] && salesPricingData[col - 1].product
                                          ? haulgeCategoryOptions.find(
                                              opt =>
                                                opt.value ===
                                                salesPricingData[col - 1][DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1]['key']],
                                            ) ?? { label: 'Standard', value: 0 } // ← default to Standard
                                          : { label: 'Standard', value: 0 }
                                      }
                                      onChange={(e, newValue) => {
                                        if (salesPricingData[col - 1] && salesPricingData[col - 1].product) {
                                          handleHaulageCategoryChange(newValue?.value ?? 0, col - 1);
                                        }
                                      }}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          size="small"
                                          sx={getExcelInputSx(isReadOnly)}
                                          inputProps={{
                                            ...params.inputProps,
                                            readOnly: isReadOnly,
                                            style: { fontSize: '0.85rem' },
                                          }}
                                        />
                                      )}
                                    />
                                    {salesPricingData[col - 1]?.haulageDetails && (
                                      <Tooltip
                                        title={
                                          <Box sx={{ fontSize: '0.75rem', lineHeight: 1.8 }}>
                                            {[
                                              ['Base Rate', salesPricingData[col - 1].haulageDetails.baseRate],
                                              ['Rate', salesPricingData[col - 1].haulageDetails.rate],
                                              ['Actual Rate', salesPricingData[col - 1].haulageDetails.actual_Rate],
                                              ['SC Rate', salesPricingData[col - 1].haulageDetails.scRate],
                                              ['Per Load', salesPricingData[col - 1].haulageDetails.perLoad],
                                              ['Fuel Surcharge', salesPricingData[col - 1].haulageDetails.fuelSurCharge],
                                              ['Alt. Pricing', salesPricingData[col - 1].haulageDetails.alternativePricing],
                                              ['Start KM', salesPricingData[col - 1].haulageDetails.startKm],
                                              ['End KM', salesPricingData[col - 1].haulageDetails.endKM],
                                              ['Rate Change %', salesPricingData[col - 1].haulageDetails.rateChangePercent],
                                            ].map(([label, val]) => (
                                              <Box
                                                key={label}
                                                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                <span style={{ color: '#9ca3af' }}>{label}</span>
                                                <span>{val ?? '-'}</span>
                                              </Box>
                                            ))}
                                          </Box>
                                        }
                                        placement="bottom-start"
                                        arrow>
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: '0.9rem', color: '#6b7280', cursor: 'pointer', flexShrink: 0 }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })
                          : renderColumns(col => {
                              return (
                                <TableCell size="small" key={`${row}_${col}`}>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    size="small"
                                    onFocus={e => {
                                      if (e.target.value === '0' || e.target.value === 0) {
                                        setTimeout(() => e.target.select(), 0); // select only if 0
                                      }
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Backspace') {
                                        const value = e.target.value;
                                        if (value.length <= 1) {
                                          setTimeout(() => e.target.select(), 0);
                                        }
                                      }
                                    }}
                                    sx={getExcelInputSx(isReadOnly)}
                                    inputProps={{
                                      readOnly: isReadOnly,
                                      style: {
                                        fontSize: '0.85rem',
                                      },
                                    }}
                                    value={
                                      salesPricingData[col - 1] && salesPricingData[col - 1].product
                                        ? salesPricingData[col - 1][DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1]['key']]
                                        : ''
                                    }
                                    onChange={
                                      salesPricingData[col - 1] && salesPricingData[col - 1].product
                                        ? (e, newValue) =>
                                            handleSalesPriceDataChange(
                                              DEFAULT_TABLE_LEFT_SIDE_LABEl[row - 1]['key'],
                                              e.target.value,
                                              col - 1,
                                            )
                                        : () => {}
                                    }
                                  />
                                </TableCell>
                              );
                            })}
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total H/F Cost/Ton
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',

                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['totalHF']
                            ? salesPricingData[curr - 1]['totalHF']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total Cost/Ton
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',

                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['total']
                            ? salesPricingData[curr - 1]['total']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total Profit/Ton
                    </TableCell>

                    {renderColumns(curr => {
                      const profit = salesPricingData[curr - 1]?.totalProfit ?? 0;
                      const profitPercent = salesPricingData[curr - 1]?.totalProfitPercentage ?? 0;

                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                          }}>
                          {`${profit} (${profitPercent}%)`}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total Sales Price/Product
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',

                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['totalSalesPerProduct']
                            ? salesPricingData[curr - 1]['totalSalesPerProduct']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total Purchase Price/Product
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',

                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['totalPurchasePricePerProducts']
                            ? salesPricingData[curr - 1]['totalPurchasePricePerProducts']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total H/F Cost Of Product{' '}
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',

                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['totalHFCostPerProduct']
                            ? salesPricingData[curr - 1]['totalHFCostPerProduct']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total Cost Of Product{' '}
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',

                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['totalCostPerProductIncludingAll']
                            ? salesPricingData[curr - 1]['totalCostPerProductIncludingAll']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    <TableCell size="small" sx={stickyLeftCellSx}>
                      Total Profit Per Product{' '}
                    </TableCell>

                    {renderColumns(curr => {
                      return (
                        <TableCell
                          size="small"
                          key={curr}
                          sx={{
                            backgroundColor: '#ECFDF5',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}>
                          {salesPricingData[curr - 1] && salesPricingData[curr - 1]['totalProfitPerProduct']
                            ? salesPricingData[curr - 1]['totalProfitPerProduct']
                            : 0}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* <TableRow>
                <TableCell size="small">Total For Site </TableCell>

                <TableCell colSpan={16} size="small" sx={{ padding: '0.75rem !important' }}>
                  {fullPriceForSite}
                </TableCell>
              </TableRow> */}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>

        <Grid item xs={12} md={2}>
          <Box
            component={Paper}
            sx={{
              p: 1.5,
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
            }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Total
            </Typography>
            <Grid container spacing={1}>
              {FULL_PRICE_SUMMARY.map(item => (
                <Grid item xs={12} key={item.key}>
                  <SummaryCard title={item.label} value={fullPriceForSite?.[item.key] ?? 0} color={'#ECFDF5'} boldValue />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* {selectedSiteAssesment && (
        <Box component={Paper} sx={{ padding: '0.2rem', boxShadow: 1, display: 'flex', justifyContent: 'end', gap: '1rem' }}>
          {currentSpNumber && (
            <Button onClick={savePricingData} variant="contained" color="primary">
              Save
            </Button>
          )}
          <Button onClick={closePricingVersion} variant="contained" color="primary">
            Close
          </Button>
        </Box>
      )} */}
      <ConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        confirmationFun={confirmationDialogProps.confirmFnc}
        declineFunction={confirmationDialogProps.cancleFnc}
        confirmationText={confirmationDialogProps.confirmText}
      />

      <PrevPricingVersionCard open={showPrevVersions} onClose={closeDrawer} prevVersions={prevVersions} />
    </div>
  );
};

export default TimberSalesPriceForm;
